import { useState, useEffect } from 'react';
import {
  searchUSDA, estimateWithGroq, estimateMacrosForUnit,
  estimateMacrosFromPhoto, saveMeal, updateMeal, deleteMeal,
} from '../api';
import {
  canUseAI, getRemainingAI, incrementAIUsage, FREE_LIMIT,
} from '../hooks/useAILimit';
import AILimitBanner from './AILimitBanner';

const MEAL_TYPES          = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];
const UNITS               = ['g', 'ml', 'L', 'piece', 'cup', 'tbsp',
                             'tsp', 'oz', 'serving', 'slice', 'bowl',
                             'handful'];
const WEIGHT_VOLUME_UNITS = ['g', 'ml'];

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const emptyForm = () => ({
  foodName: '', mealType: 'Breakfast',
  calories: '', protein: '', carbs: '', fat: '',
  quantity: 100, unit: 'g', date: todayStr(),
});

const glass = {
  background:           'rgba(255,255,255,0.07)',
  backdropFilter:       'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border:               '1px solid rgba(255,255,255,0.12)',
  borderRadius:         16,
};

const inputStyle = {
  padding: '12px 14px', borderRadius: 12,
  border: '1px solid rgba(255,255,255,0.15)',
  background: 'rgba(255,255,255,0.07)',
  color: '#fff', width: '100%',
  boxSizing: 'border-box', fontSize: 15,
  marginBottom: 12, outline: 'none',
};

const labelStyle = {
  fontSize: 12, color: 'rgba(255,255,255,0.5)',
  marginBottom: 6, display: 'block', fontWeight: 500,
  textTransform: 'uppercase', letterSpacing: 0.5,
};

export default function LogMeal({
  onMealLogged, editMeal, onCancelEdit, isPro, onUpgrade,
}) {
  const [query,        setQuery]      = useState('');
  const [status,       setStatus]     = useState('idle');
  const [form,         setForm]       = useState(emptyForm());
  const [dataSource,   setSource]     = useState('MANUAL');
  const [usdaResults,  setUSDA]       = useState([]);
  const [aiSuggestion, setAiSugg]     = useState(null);
  const [aiLoading,    setAiLoading]  = useState(false);
  const [unitEstimate, setUnitEst]    = useState(null);
  const [unitLoading,  setUnitLoading] = useState(false);
  const [isEditing,    setIsEditing]  = useState(false);
  const [remainingAI,  setRemainingAI] = useState(
    getRemainingAI(isPro)
  );

  // Update remaining when isPro changes
  useEffect(() => {
    setRemainingAI(getRemainingAI(isPro));
  }, [isPro]);

  // Pre-fill form when editing
  useEffect(() => {
    if (editMeal) {
      setForm({
        foodName:  editMeal.foodName  || '',
        mealType:  editMeal.mealType  || 'Breakfast',
        calories:  editMeal.calories  || '',
        protein:   editMeal.protein   || '',
        carbs:     editMeal.carbs     || '',
        fat:       editMeal.fat       || '',
        quantity:  editMeal.quantity  || 100,
        unit:      editMeal.unit      || 'g',
        date:      editMeal.timestamp
          ? editMeal.timestamp.split('T')[0] : todayStr(),
        timestamp: editMeal.timestamp,
      });
      setSource(editMeal.dataSource || 'MANUAL');
      setIsEditing(true);
      setStatus('found');
      setQuery('');
      setUSDA([]);
      setAiSugg(null);
    } else {
      setIsEditing(false);
      setStatus('idle');
      setForm(emptyForm());
    }
  }, [editMeal]);

  // ── Search ─────────────────────────────────────────────────
  async function handleSearch() {
    if (!query.trim()) return;
    setStatus('searching');
    setUSDA([]);
    setAiSugg(null);
    setUnitEst(null);

    let usdaItems = [];
    try {
      usdaItems = await searchUSDA(query);
      setUSDA(usdaItems);
    } catch (e) { console.error('USDA error', e); }

    // Only call Groq if user has quota
    if (canUseAI(isPro)) {
      setAiLoading(true);
      try {
        const aiResult = await estimateWithGroq(query);
        setAiSugg({ ...aiResult, foodName: query });
        incrementAIUsage();
        setRemainingAI(getRemainingAI(isPro));
      } catch (e) { console.error('Groq error', e); }
      setAiLoading(false);
    }

    if (usdaItems.length === 0 && !canUseAI(isPro)) {
      switchToManual();
      return;
    }
    if (usdaItems.length > 0 || canUseAI(isPro)) {
      setStatus('usda_results');
    } else {
      switchToManual();
    }
  }

  // ── Photo capture ──────────────────────────────────────────
  async function handlePhotoCapture(e) {
    const file = e.target.files[0];
    if (!file) return;

    if (!canUseAI(isPro)) {
      onUpgrade && onUpgrade(
        'Photo logging uses your daily AI quota. Upgrade for unlimited.'
      );
      e.target.value = '';
      return;
    }

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      alert('Image too large. Please use an image under 4MB');
      return;
    }

    setStatus('photo_analyzing');
    setUSDA([]);
    setAiSugg(null);
    setUnitEst(null);

    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const result = await estimateMacrosFromPhoto(base64, file.type);

      setForm(f => ({
        ...f,
        foodName:       result.foodName,
        calories:       result.calories,
        protein:        result.protein,
        carbs:          result.carbs,
        fat:            result.fat,
        quantity:       result.grams,
        unit:           'g',
        _base_calories: result.calories,
        _base_protein:  result.protein,
        _base_carbs:    result.carbs,
        _base_fat:      result.fat,
      }));
      setSource('AI_ESTIMATED');
      setStatus('found');
      incrementAIUsage();
      setRemainingAI(getRemainingAI(isPro));

    } catch (e) {
      console.error('Photo analysis error', e);
      alert('Could not analyse photo. Please try again or enter manually.');
      setStatus('idle');
    }
    e.target.value = '';
  }

  // ── Select food ────────────────────────────────────────────
  function selectFood(food) {
    setForm(f => ({
      ...food,
      mealType:       f.mealType,
      date:           f.date,
      unit:           'g',
      quantity:       100,
      _base_calories: food.calories,
      _base_protein:  food.protein,
      _base_carbs:    food.carbs,
      _base_fat:      food.fat,
    }));
    setSource(food.dataSource || 'MANUAL');
    setUSDA([]);
    setAiSugg(null);
    setUnitEst(null);
    setStatus('found');
  }

  function switchToManual() {
    setForm(f => ({
      ...emptyForm(), foodName: query,
      mealType: f.mealType, date: f.date,
    }));
    setSource('MANUAL');
    setAiSugg(null);
    setUnitEst(null);
    setStatus('manual');
  }

  // ── Quantity / unit changes ────────────────────────────────
  function handleQuantityChange(newQuantity) {
    const isWV = WEIGHT_VOLUME_UNITS.includes(form.unit);
    if (isWV && dataSource !== 'MANUAL' && form._base_calories) {
      const ratio = newQuantity / 100;
      setForm(f => ({
        ...f, quantity: newQuantity,
        calories: Math.round(f._base_calories * ratio),
        protein:  Math.round(f._base_protein  * ratio),
        carbs:    Math.round(f._base_carbs    * ratio),
        fat:      Math.round(f._base_fat      * ratio),
      }));
    } else {
      setForm(f => ({ ...f, quantity: newQuantity }));
    }
    setUnitEst(null);
  }

  async function handleUnitChange(newUnit) {
    const isWV = WEIGHT_VOLUME_UNITS.includes(newUnit);
    setForm(f => ({ ...f, unit: newUnit, quantity: isWV ? 100 : 1 }));
    setUnitEst(null);

    if (isWV && form._base_calories) {
      setForm(f => ({
        ...f, unit: newUnit, quantity: 100,
        calories: Math.round(f._base_calories),
        protein:  Math.round(f._base_protein),
        carbs:    Math.round(f._base_carbs),
        fat:      Math.round(f._base_fat),
      }));
      return;
    }

    if (!isWV && form.foodName) {
      setUnitLoading(true);
      try {
        const result = await estimateMacrosForUnit(
          form.foodName, 1, newUnit
        );
        setUnitEst({
          estimatedGrams: result.estimatedGrams,
          unit: newUnit, quantity: 1,
        });
        setForm(f => ({
          ...f, unit: newUnit, quantity: 1,
          calories: result.calories, protein: result.protein,
          carbs: result.carbs, fat: result.fat,
          _base_calories: result.calories,
          _base_protein:  result.protein,
          _base_carbs:    result.carbs,
          _base_fat:      result.fat,
        }));
        setSource('AI_ESTIMATED');
      } catch (e) {
        setForm(f => ({
          ...f, unit: newUnit, quantity: 1,
          calories: '', protein: '', carbs: '', fat: '',
        }));
      }
      setUnitLoading(false);
    }
  }

  async function handleNonWeightQuantityChange(newQuantity) {
    setForm(f => ({ ...f, quantity: newQuantity }));
    setUnitEst(null);
    if (!WEIGHT_VOLUME_UNITS.includes(form.unit)
        && form.foodName && newQuantity > 0) {
      setUnitLoading(true);
      try {
        const result = await estimateMacrosForUnit(
          form.foodName, newQuantity, form.unit
        );
        setUnitEst({
          estimatedGrams: result.estimatedGrams,
          unit: form.unit, quantity: newQuantity,
        });
        setForm(f => ({
          ...f, quantity: newQuantity,
          calories: result.calories, protein: result.protein,
          carbs: result.carbs, fat: result.fat,
        }));
        setSource('AI_ESTIMATED');
      } catch (e) { console.error(e); }
      setUnitLoading(false);
    }
  }

  // ── Save new meal ──────────────────────────────────────────
  async function handleSave() {
    if (!form.foodName || !form.calories) {
      alert('Food name and calories are required');
      return;
    }
    setStatus('saving');
    try {
      await saveMeal({
        foodName:   form.foodName,
        mealType:   form.mealType,
        calories:   parseFloat(form.calories) || 0,
        protein:    parseFloat(form.protein)  || 0,
        carbs:      parseFloat(form.carbs)    || 0,
        fat:        parseFloat(form.fat)      || 0,
        quantity:   parseFloat(form.quantity) || 0,
        unit:       form.unit,
        dataSource: dataSource,
        date:       form.date,
      });
      setQuery('');
      setForm(emptyForm());
      setStatus('idle');
      setAiSugg(null);
      setUnitEst(null);
      onMealLogged();
    } catch (e) {
      alert('Error saving meal: ' + e.message);
      setStatus('found');
    }
  }

  // ── Update existing meal ───────────────────────────────────
  async function handleUpdate() {
    if (!form.foodName || !form.calories) {
      alert('Food name and calories are required');
      return;
    }
    setStatus('saving');
    try {
      await updateMeal({
        timestamp:  form.timestamp,
        foodName:   form.foodName,
        mealType:   form.mealType,
        calories:   parseFloat(form.calories) || 0,
        protein:    parseFloat(form.protein)  || 0,
        carbs:      parseFloat(form.carbs)    || 0,
        fat:        parseFloat(form.fat)      || 0,
        quantity:   parseFloat(form.quantity) || 0,
        unit:       form.unit,
        dataSource: dataSource,
      });
      setIsEditing(false);
      setForm(emptyForm());
      setStatus('idle');
      onMealLogged();
      if (onCancelEdit) onCancelEdit();
    } catch (e) {
      alert('Error updating meal: ' + e.message);
      setStatus('found');
    }
  }

  // ── Delete meal ────────────────────────────────────────────
  async function handleDelete() {
    if (!window.confirm(`Delete "${form.foodName}"?`)) return;
    setStatus('saving');
    try {
      await deleteMeal(form.timestamp);
      setIsEditing(false);
      setForm(emptyForm());
      setStatus('idle');
      onMealLogged();
      if (onCancelEdit) onCancelEdit();
    } catch (e) {
      alert('Error deleting meal: ' + e.message);
      setStatus('found');
    }
  }

  const showForm       = ['found', 'manual', 'saving'].includes(status);
  const isWeightVolume = WEIGHT_VOLUME_UNITS.includes(form.unit);
  const isBusy         = status === 'saving'
    || unitLoading || status === 'photo_analyzing';

  return (
    <div>
      {/* Title */}
      <div style={{ display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{
          margin: 0, fontSize: 22, fontWeight: 700,
          background: 'linear-gradient(135deg, #fff 0%, #a0c4ff 100%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          {isEditing ? '✏️ Edit Meal' : '➕ Log a Meal'}
        </h2>
        {isEditing && (
          <button onClick={() => {
            setIsEditing(false);
            setForm(emptyForm());
            setStatus('idle');
            if (onCancelEdit) onCancelEdit();
          }} style={{
            fontSize: 13, color: 'rgba(255,255,255,0.5)',
            background: 'rgba(255,255,255,0.07)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
          }}>
            Cancel
          </button>
        )}
      </div>

      {/* AI limit banner */}
      {!isEditing && (
        <AILimitBanner
          remaining={remainingAI}
          onUpgrade={() => onUpgrade && onUpgrade()}
        />
      )}

      {/* Search bar — only when not editing */}
      {!isEditing && (
        <div style={{ ...glass, padding: 16, marginBottom: 16 }}>
          {/* Hidden file input */}
          <input
            type="file" id="photo-input"
            accept="image/*" capture="environment"
            onChange={handlePhotoCapture}
            style={{ display: 'none' }}
          />

          <div style={{ display: 'flex', gap: 10 }}>
            {/* Camera button */}
            <button
              onClick={() => document.getElementById('photo-input').click()}
              disabled={isBusy}
              title="Take or upload a photo of your meal"
              style={{
                padding: '12px 14px', borderRadius: 12,
                border: '1px solid rgba(168,85,247,0.4)',
                background: 'rgba(168,85,247,0.2)',
                color: '#c084fc', cursor: 'pointer',
                fontSize: 18, transition: 'all 0.2s',
                opacity: isBusy ? 0.6 : 1,
              }}>
              📷
            </button>

            {/* Search input */}
            <input
              placeholder="Search food (e.g. dal, chicken, oats)..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
            />

            {/* Search button */}
            <button
              onClick={handleSearch}
              disabled={isBusy}
              style={{
                padding: '12px 18px', borderRadius: 12,
                border: 'none',
                background: 'linear-gradient(135deg, #5282ff, #a855f7)',
                color: '#fff', cursor: 'pointer', fontSize: 18,
                boxShadow: '0 4px 15px rgba(82,130,255,0.4)',
                opacity: isBusy ? 0.7 : 1,
              }}>
              🔍
            </button>
          </div>

          {status === 'idle' && (
            <button onClick={switchToManual} style={{
              marginTop: 10, fontSize: 13,
              color: 'rgba(255,255,255,0.4)',
              background: 'none', border: 'none',
              cursor: 'pointer', padding: 0,
            }}>
              ✏️ Enter manually instead
            </button>
          )}
          {status === 'searching' && (
            <div style={{ marginTop: 10, fontSize: 13,
              color: '#60a5fa' }}>
              🔍 Searching USDA database...
            </div>
          )}
          {status === 'photo_analyzing' && (
            <div style={{ marginTop: 10, fontSize: 13,
              color: '#c084fc' }}>
              📷 Analysing your photo with AI...
            </div>
          )}
        </div>
      )}

      {/* USDA results + AI suggestion */}
      {status === 'usda_results' && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)',
            marginBottom: 10, textTransform: 'uppercase',
            letterSpacing: 0.5 }}>
            Select a food
          </div>

          {/* USDA results */}
          {usdaResults.map((food, i) => (
            <div key={i} onClick={() => selectFood(food)} style={{
              ...glass, padding: '14px 16px', marginBottom: 8,
              cursor: 'pointer',
              borderColor: 'rgba(96,165,250,0.3)',
            }}>
              <div style={{ display: 'flex',
                justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 600, fontSize: 14,
                  flex: 1, marginRight: 8 }}>
                  ✅ {food.foodName}
                </div>
                <div style={{
                  fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
                  background: 'linear-gradient(135deg, #ff6b6b, #feca57)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  {food.calories} kcal
                </div>
              </div>
              <div style={{ fontSize: 12,
                color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                per 100g · P:{food.protein}g · C:{food.carbs}g
                · F:{food.fat}g
              </div>
            </div>
          ))}

          {/* AI loading */}
          {aiLoading && (
            <div style={{ ...glass, padding: '14px 16px',
              marginBottom: 8,
              borderColor: 'rgba(168,85,247,0.3)', opacity: 0.7 }}>
              <div style={{ fontSize: 14,
                color: 'rgba(255,255,255,0.5)' }}>
                🤖 Getting AI estimate for "{query}"...
              </div>
            </div>
          )}

          {/* AI suggestion */}
          {aiSuggestion && !aiLoading && (
            <div onClick={() => selectFood(aiSuggestion)} style={{
              ...glass, padding: '14px 16px', marginBottom: 8,
              cursor: 'pointer',
              background: 'rgba(168,85,247,0.12)',
              borderColor: 'rgba(168,85,247,0.4)',
            }}>
              <div style={{ display: 'flex',
                justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 600, fontSize: 14,
                  flex: 1, marginRight: 8 }}>
                  🤖 {aiSuggestion.foodName}
                  <span style={{ fontSize: 11,
                    color: 'rgba(168,85,247,0.9)',
                    marginLeft: 8, fontWeight: 400 }}>
                    AI Estimated
                  </span>
                </div>
                <div style={{
                  fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
                  background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  {aiSuggestion.calories} kcal
                </div>
              </div>
              <div style={{ fontSize: 12,
                color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                per 100g · P:{aiSuggestion.protein}g
                · C:{aiSuggestion.carbs}g · F:{aiSuggestion.fat}g
                · ⚠️ approximate
              </div>
            </div>
          )}

          {/* Upgrade nudge when AI limit hit */}
          {!canUseAI(isPro) && !aiLoading && (
            <div onClick={() => onUpgrade && onUpgrade()}
              style={{
                ...glass, padding: '14px 16px', marginBottom: 8,
                cursor: 'pointer',
                background: 'rgba(82,130,255,0.1)',
                borderColor: 'rgba(82,130,255,0.3)',
              }}>
              <div style={{ fontSize: 14, color: '#818cf8',
                fontWeight: 600 }}>
                ⭐ Upgrade to Pro for unlimited AI lookups
              </div>
              <div style={{ fontSize: 12,
                color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                {FREE_LIMIT} free AI lookups/day on free plan
              </div>
            </div>
          )}

          {/* Manual fallback */}
          <button onClick={switchToManual} style={{
            width: '100%', padding: '12px', borderRadius: 12,
            marginTop: 4,
            background: 'rgba(255,255,255,0.05)',
            border: '1px dashed rgba(255,255,255,0.2)',
            color: 'rgba(255,255,255,0.4)',
            cursor: 'pointer', fontSize: 13,
          }}>
            ✏️ Enter macros manually instead
          </button>
        </div>
      )}

      {/* Meal form */}
      {showForm && (
        <div style={{ ...glass, padding: 20 }}>

          {/* AI badge */}
          {dataSource === 'AI_ESTIMATED' && (
            <div style={{
              background: 'rgba(168,85,247,0.15)',
              border: '1px solid rgba(168,85,247,0.3)',
              borderRadius: 10, padding: '10px 14px',
              marginBottom: 16, fontSize: 13, color: '#c084fc',
            }}>
              🤖 <strong>AI Estimated</strong> — values are approximate.
              Feel free to edit.
            </div>
          )}

          {/* Food name */}
          <label style={labelStyle}>Food Name</label>
          <input
            placeholder="e.g. Chicken Breast"
            value={form.foodName}
            onChange={e => setForm(f => ({
              ...f, foodName: e.target.value
            }))}
            style={inputStyle}
          />

          {/* Date */}
          <label style={labelStyle}>📅 Date</label>
          <input
            type="date" value={form.date}
            onChange={e => setForm(f => ({
              ...f, date: e.target.value
            }))}
            style={inputStyle}
          />

          {/* Meal type */}
          <label style={labelStyle}>Meal Type</label>
          <select
            value={form.mealType}
            onChange={e => setForm(f => ({
              ...f, mealType: e.target.value
            }))}
            style={{ ...inputStyle, appearance: 'none' }}>
            {MEAL_TYPES.map(m => (
              <option key={m} style={{ background: '#1a1a2e' }}>{m}</option>
            ))}
          </select>

          {/* Quantity + Unit */}
          <label style={labelStyle}>
            ⚖️ Quantity
            {isWeightVolume && dataSource !== 'MANUAL' && (
              <span style={{ color: '#60a5fa',
                textTransform: 'none', marginLeft: 6 }}>
                — macros auto-recalculate
              </span>
            )}
          </label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input
              type="number" value={form.quantity}
              onChange={e => {
                const val = parseFloat(e.target.value) || 0;
                isWeightVolume
                  ? handleQuantityChange(val)
                  : handleNonWeightQuantityChange(val);
              }}
              style={{ ...inputStyle, marginBottom: 0, flex: 2 }}
            />
            <select
              value={form.unit}
              onChange={e => handleUnitChange(e.target.value)}
              style={{ ...inputStyle, marginBottom: 0, flex: 1,
                appearance: 'none' }}>
              {UNITS.map(u => (
                <option key={u} style={{ background: '#1a1a2e' }}>
                  {u}
                </option>
              ))}
            </select>
          </div>

          {unitLoading && (
            <div style={{ fontSize: 13, color: '#a855f7',
              marginBottom: 12 }}>
              🤖 Calculating macros for {form.quantity} {form.unit}...
            </div>
          )}

          {unitEstimate && !unitLoading && (
            <div style={{
              background: 'rgba(96,165,250,0.1)',
              border: '1px solid rgba(96,165,250,0.3)',
              borderRadius: 10, padding: '8px 14px',
              marginBottom: 12, fontSize: 13, color: '#93c5fd',
            }}>
              🤖 <strong>{unitEstimate.quantity} {unitEstimate.unit}</strong>
              {' '}≈ <strong>{unitEstimate.estimatedGrams}g</strong>
              <span style={{ color: 'rgba(255,255,255,0.3)',
                marginLeft: 6 }}>
                AI estimated weight
              </span>
            </div>
          )}

          {/* Macro grid */}
          <label style={labelStyle}>Nutritional Values</label>
          <div style={{ display: 'grid',
            gridTemplateColumns: '1fr 1fr', gap: 10,
            marginBottom: 20 }}>
            {[
              ['calories', '🔥 Calories (kcal)', '#ff6b6b'],
              ['protein',  '💪 Protein (g)',      '#60a5fa'],
              ['carbs',    '🌾 Carbs (g)',         '#fbbf24'],
              ['fat',      '🥑 Fat (g)',           '#34d399'],
            ].map(([key, label, color]) => (
              <div key={key}>
                <label style={{ ...labelStyle, color }}>{label}</label>
                <input
                  type="number" value={form[key]}
                  onChange={e => setForm(f => ({
                    ...f, [key]: e.target.value
                  }))}
                  disabled={unitLoading}
                  style={{
                    ...inputStyle, marginBottom: 0,
                    borderColor: `${color}44`,
                    background: unitLoading
                      ? 'rgba(255,255,255,0.03)'
                      : 'rgba(255,255,255,0.07)',
                  }}
                />
              </div>
            ))}
          </div>

          {/* Save / Update button */}
          <button
            onClick={isEditing ? handleUpdate : handleSave}
            disabled={isBusy}
            style={{
              width: '100%', padding: 16, borderRadius: 14,
              border: 'none',
              background: isBusy ? 'rgba(255,255,255,0.1)'
                : isEditing
                ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                : 'linear-gradient(135deg, #5282ff, #a855f7)',
              color: '#fff', fontSize: 16, fontWeight: 700,
              cursor: 'pointer',
              marginBottom: isEditing ? 10 : 0,
              boxShadow: isBusy ? 'none'
                : isEditing
                ? '0 4px 20px rgba(245,158,11,0.3)'
                : '0 4px 20px rgba(82,130,255,0.4)',
              transition: 'all 0.2s',
            }}>
            {isBusy
              ? (isEditing ? 'Updating...' : 'Saving...')
              : isEditing ? '💾 Update Meal' : '✅ Add Meal'}
          </button>

          {/* Delete — edit mode only */}
          {isEditing && (
            <button
              onClick={handleDelete}
              disabled={isBusy}
              style={{
                width: '100%', padding: 14, borderRadius: 14,
                background: 'rgba(239,68,68,0.12)',
                border: '1px solid rgba(239,68,68,0.3)',
                color: '#fca5a5', fontSize: 15, fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.2s',
              }}>
              🗑️ Delete Meal
            </button>
          )}
        </div>
      )}
    </div>
  );
}